import React, { useEffect, useState } from "react";
import { format, startOfMonth } from "date-fns";
import SkeletonLoader from "../../../../components/skeleton-loader";
import styles from "./style.module.scss";
import { fetchResidualScorecard } from "../../../../services/residuals";
import useDomainStore from "../../../../stores/domain";
import moment from "moment";
import useResidualStore from "../../../../stores/residuals";

const ScoreItem = ({ title, amount, percentage, count, description, color }) => {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  return (
    <div className={styles.score_item}>
      <div className={styles.score_header}>
        <h6>{title}</h6>
        <span>{formattedAmount}</span>
      </div>
      <div className={styles.score_bar}>
        <div
          className={styles.score_progress_bg}
          style={{ backgroundColor: color }}
        />
        <div
          className={styles.score_progress}
          style={{
            width: `${percentage}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <div className={styles.score_footer}>
        <span className={styles.percentage}>{description}</span>
        <span className={styles.count}>{count}</span>
      </div>
    </div>
  );
};

function ResidualScoreCard() {
  const { isKurvDomain } = useDomainStore();
  const filters = useResidualStore((state) => state.filters);
  const [scorecard, setScorecard] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCurrentMonthData = async () => {
      setLoading(true);
      try {
        const dateRange = {
          startDate: moment().subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
          endDate: moment().subtract(2, 'month').endOf('month').format('YYYY-MM-DD'),
          rangeType: "MTD"
        };
        const data = await fetchResidualScorecard(filters);
        setScorecard(data || {});
      } catch (error) {
        console.error('Error fetching current month scorecard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentMonthData();
  }, [filters]);

  if (loading) {
    return (
      <div className={styles.score_card}>
        <div className={styles.header}>
          <SkeletonLoader variant="rectangular" height={24} width={160} />
        </div>
        <div className={styles.score_list}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.score_item}>
              <SkeletonLoader variant="rectangular" height={16} width={120} />
              <SkeletonLoader variant="rectangular" height={7} width="100%" />
              <SkeletonLoader variant="rectangular" height={12} width={80} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.score_card} ${isKurvDomain ? styles.kurv_theme : ""}`}>
      <div className={styles.header}>
        <h5>Residual Scorecard</h5>
      </div>

      <div className={styles.score_list}>
        <ScoreItem
          title="Active merchants"
          amount={scorecard?.active_merchant?.mtd_volume || 0}
          percentage={scorecard?.active_merchant?.percentage || 0}
          count={`${scorecard?.active_merchant?.account_count || 0} accounts`}
          description={`${scorecard?.active_merchant?.percentage || 0}% of all merchants`}
          color="#37B450"
        />

        <ScoreItem
          title="Idle Merchants"
          amount={scorecard?.idle_merchant?.mtd_volume || 0}
          percentage={scorecard?.idle_merchant?.percentage || 0}
          count={`${scorecard?.idle_merchant?.account_count || 0} accounts`}
          description={`${scorecard?.idle_merchant?.percentage || 0}% of all merchants`}
          color="#EBB800"
        />

        <ScoreItem
          title="Merchants Attrition"
          amount={scorecard?.attrition_merchant?.mtd_volume || 0}
          percentage={scorecard?.attrition_merchant?.percentage || 0}
          count={`${scorecard?.attrition_merchant?.account_count || 0} accounts`}
          description={`${scorecard?.attrition_merchant?.percentage || 0}% of active merchants`}
          color="#B61D08"
        />

        <div className={styles.pipeline_card}>
          <ScoreItem
            title="Deal pipeline"
            amount={scorecard?.deal_pipeline_merchant?.mtd_volume || 0}
            percentage={scorecard?.deal_pipeline_merchant?.percentage || 0}
            count={`${scorecard?.deal_pipeline_merchant?.account_count || 0} apps awaiting`}
            description={`${scorecard?.deal_pipeline_merchant?.percentage || 0}% of all applications`}
            color="#4C70EF"
          />
        </div>
      </div>
    </div>
  );
}

export default ResidualScoreCard;